import React from 'react';
import { useDispatch } from 'react-redux';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    Box,
    Menu,
    Link,
    styled,
    alpha
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import dayjs from 'dayjs';
import * as actions from '../redux.actions';
import { COLUMN_WIDTHS, ROWS_PER_PAGE_OPTIONS } from '../common';

const StyledMenuBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    gap: theme.spacing(2)
}));

const menuLinkStyles = (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    height: '32px',
    padding: '8px 12px',
    border: '1px solid',
    borderColor: 'primary.main',
    borderRadius: '4px',
    color: 'primary.main',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: 400,
    cursor: 'pointer',
    pl: 3,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        textDecoration: 'none',
        borderColor: theme.palette.primary.dark,
        color: theme.palette.primary.dark
    },
    '&:focus': {
        outline: 'none',
        backgroundColor: alpha(theme.palette.primary.main, 0.1)
    }
});

const deleteLinkStyles = (theme) => ({
    ...menuLinkStyles(theme),
    borderColor: 'error.main',
    color: 'error.main',
    '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.1),
        textDecoration: 'none',
        borderColor: 'error.dark',
        color: 'error.dark'
    },
    '&:focus': {
        outline: 'none',
        backgroundColor: alpha(theme.palette.error.main, 0.1)
    }
});

const TemplateTable = ({ templates, pagination, onPageChange, onPageSizeChange }) => {
    const dispatch = useDispatch();
    const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
    const [activeTemplate, setActiveTemplate] = React.useState(null);

    const handleEdit = (template) => {
        dispatch(actions.openTemplateDialog(template));
    };

    const handleDelete = (template) => {
        dispatch(actions.openDeleteDialog(template));
    };

    const handleManageGoals = (template) => {
        dispatch(actions.openManageGoalsDialog(template));
        dispatch(actions.fetchTemplateWithGoals(template.id));
    };

    const openMenu = (event, template) => {
        setMenuAnchorEl(event.currentTarget);
        setActiveTemplate(template);
    };

    const closeMenu = () => {
        setMenuAnchorEl(null);
        setActiveTemplate(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return dayjs(dateString).format('MMM DD, YYYY');
        } catch {
            return dateString;
        }
    };

    return (
        <>
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell 
                            style={{ minWidth: 350, width: COLUMN_WIDTHS.name }}
                            sx={{
                                fontWeight: 600,
                                backgroundColor: 'background.default',
                                borderBottom: 1,
                                borderBottomColor: 'divider'
                            }}
                        >
                            Template Name
                        </TableCell>
                        <TableCell 
                            style={{ minWidth: 300, width: COLUMN_WIDTHS.description }}
                            sx={{
                                fontWeight: 600,
                                backgroundColor: 'background.default',
                                borderBottom: 1,
                                borderBottomColor: 'divider'
                            }}
                        >
                            Description
                        </TableCell>
                        <TableCell 
                            align="center"
                            style={{ minWidth: 140, width: COLUMN_WIDTHS.goalsCount }}
                            sx={{
                                fontWeight: 600,
                                backgroundColor: 'background.default',
                                borderBottom: 1,
                                borderBottomColor: 'divider'
                            }}
                        >
                            Goals
                        </TableCell>
                        <TableCell 
                            style={{ minWidth: 180, width: COLUMN_WIDTHS.updatedBy }}
                            sx={{
                                fontWeight: 600,
                                backgroundColor: 'background.default',
                                borderBottom: 1,
                                borderBottomColor: 'divider'
                            }}
                        >
                            Updated By
                        </TableCell>
                        <TableCell 
                            style={{ minWidth: 180, width: COLUMN_WIDTHS.updatedAt }}
                            sx={{
                                fontWeight: 600,
                                backgroundColor: 'background.default',
                                borderBottom: 1,
                                borderBottomColor: 'divider'
                            }}
                        >
                            Updated At
                        </TableCell>
                        <TableCell 
                            align="center"
                            style={{ minWidth: 150, width: COLUMN_WIDTHS.actions }}
                            sx={{
                                fontWeight: 600,
                                backgroundColor: 'background.default',
                                borderBottom: 1,
                                borderBottomColor: 'divider'
                            }}
                        >
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {templates.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                No templates found
                            </TableCell>
                        </TableRow>
                    ) : (
                        templates.map((template) => {
                            const goalsCount = template.goals?.length || template.goalsCount || 0;
                            
                            return (
                            <TableRow 
                                key={template.id} 
                                hover
                                sx={(theme) => ({
                                    '&:last-child td': { border: 0 },
                                    '&:hover': { backgroundColor: alpha(theme.palette.divider, 0.06) }
                                })}
                            >
                                <TableCell>
                                    <strong>{template.name}</strong>
                                </TableCell>
                                <TableCell>
                                    <Tooltip title={template.description || ''} placement="top">
                                        <span
                                            style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {template.description || '-'}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="center">
                                    <Box
                                        onClick={goalsCount > 0 ? () => handleManageGoals(template) : undefined}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 24,
                                            height: 24,
                                            borderRadius: '50%',
                                            border: '1px solid',
                                            borderColor: goalsCount > 0 ? 'primary.main' : 'grey.300',
                                            backgroundColor: 'transparent',
                                            color: goalsCount > 0 ? 'primary.main' : 'grey.600',
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                            cursor: goalsCount > 0 ? 'pointer' : 'default',
                                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&::before': goalsCount > 0 ? {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                borderRadius: '50%',
                                                backgroundColor: 'primary.main',
                                                transform: 'scale(0)',
                                                transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                                zIndex: 0,
                                                opacity: 1
                                            } : {},
                                            ...(goalsCount > 0 && {
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    transform: 'scale(1.08)',
                                                    boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                                                    '&::before': {
                                                        transform: 'scale(1)',
                                                        opacity: 1
                                                    }
                                                },
                                                '&:hover > span': {
                                                    transform: 'scale(1)',
                                                    color: (theme) => theme.palette.getContrastText(theme.palette.primary.main)
                                                },
                                                '&:active': {
                                                    transform: 'scale(0.96)',
                                                    boxShadow: (theme) => `0 2px 6px ${alpha(theme.palette.primary.main, 0.18)}`
                                                }
                                            })
                                        }}
                                    >
                                        <span style={{ position: 'relative', zIndex: 1, transition: 'color 0.25s ease' }}>
                                            {goalsCount}
                                        </span>
                                    </Box>
                                </TableCell>
                                <TableCell>{template.updatedBy || template.createdBy || '-'}</TableCell>
                                <TableCell>{formatDate(template.updatedAt || template.createdAt)}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title="More" placement="top">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => openMenu(e, template)}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>

        <Menu
            id="template-actions-menu"
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={closeMenu}
            MenuListProps={{
                'aria-labelledby': 'template-actions-button'
            }}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <StyledMenuBox>
                <Link
                    component="button"
                    onClick={(e) => {
                        e.preventDefault();
                        if (activeTemplate) handleManageGoals(activeTemplate);
                        closeMenu();
                    }}
                    sx={(theme) => menuLinkStyles(theme)}
                >
                    <AssignmentIcon sx={{ marginRight: 1, fontSize: '18px' }} />
                    Manage Goals
                </Link>
                <Link
                    component="button"
                    onClick={(e) => {
                        e.preventDefault();
                        if (activeTemplate) handleEdit(activeTemplate);
                        closeMenu();
                    }}
                    sx={(theme) => menuLinkStyles(theme)}
                >
                    <EditIcon sx={{ marginRight: 1, fontSize: '18px' }} />
                    Edit
                </Link>
                <Link
                    component="button"
                    onClick={(e) => {
                        e.preventDefault();
                        if (activeTemplate) handleDelete(activeTemplate);
                        closeMenu();
                    }}
                    sx={(theme) => deleteLinkStyles(theme)}
                >
                    <DeleteIcon sx={{ marginRight: 1, fontSize: '18px' }} />
                    Delete
                </Link>
            </StyledMenuBox>
        </Menu>

        <TablePagination
            component="div"
            count={pagination.total}
            page={(pagination.offset || 1) - 1}
            onPageChange={(event, newPage) => onPageChange(newPage + 1)}
            rowsPerPage={pagination.limit}
            onRowsPerPageChange={(event) => onPageSizeChange(parseInt(event.target.value, 10))}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Rows per page:"
            sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                '& .MuiTablePagination-toolbar': {
                    justifyContent: 'space-between',
                    paddingLeft: 2,
                    paddingRight: 2
                },
                '& .MuiTablePagination-spacer': {
                    display: 'none'
                },
                '& .MuiTablePagination-selectLabel': {
                    margin: 0
                },
                '& .MuiTablePagination-displayedRows': {
                    margin: 0,
                    marginLeft: 'auto'
                }
            }}
        />
        </>
    );
};

export default TemplateTable;
